import { QueryClient, dehydrate, hydrate, type DehydratedState } from '@tanstack/react-query';
import { isApiError } from '@kidova/api-client';

/**
 * Query cache kept in sessionStorage so a reload paints the last known data in the first frame
 * instead of skeletons (the no-flicker rule). sessionStorage, not localStorage: family data should
 * not outlive the tab on a shared computer, and sign-out clears it (ADR 0602).
 */
const KEY = 'kd-cache';
// Bump when a response shape changes so an old tab never renders a stale shape.
const BUSTER = 'v1';
// Older snapshots are dropped rather than shown; a live refetch follows every restore anyway.
const MAX_AGE_MS = 60 * 60_000;
const WRITE_DELAY_MS = 500;

interface Snapshot {
  buster: string;
  savedAt: number;
  state: DehydratedState;
}

// Live events mark changed data stale at once (lib/live.ts), so age only covers missed events; a
// minute matches the server's own cache of the home read model (HOME_CACHE_TTL_S, application/common.py).
const STALE_MS = 60_000;
// A read refused with 429 is retried after its Retry-After; the page keeps what it shows meanwhile.
const RATE_LIMITED_RETRIES = 3;

export function shouldRetry(count: number, error: unknown): boolean {
  if (isApiError(error) && error.status === 429) return count < RATE_LIMITED_RETRIES;
  return count < 2 && !(isApiError(error) && error.status >= 400 && error.status < 500);
}

export function retryDelay(count: number, error: unknown): number {
  if (isApiError(error) && error.status === 429) return (error.retryAfterS ?? 1) * 1000;
  // TanStack Query's own default for every other failure.
  return Math.min(1000 * 2 ** count, 30_000);
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: STALE_MS,
        gcTime: MAX_AGE_MS,
        refetchOnWindowFocus: true,
        retry: shouldRetry,
        retryDelay,
      },
      mutations: { retry: false },
    },
  });
}

export function restoreCache(client: QueryClient): void {
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (!raw) return;
    const snapshot = JSON.parse(raw) as Snapshot;
    if (snapshot.buster !== BUSTER || Date.now() - snapshot.savedAt > MAX_AGE_MS) {
      window.sessionStorage.removeItem(KEY);
      return;
    }
    hydrate(client, snapshot.state);
  } catch {
    // A damaged snapshot only costs one skeleton.
  }
}

export function persistCache(client: QueryClient): () => void {
  let timer: number | undefined;
  const save = () => {
    timer = undefined;
    try {
      const state = dehydrate(client, {
        // The session carries the CSRF token, which stays in memory only (§11.9).
        shouldDehydrateQuery: (query) =>
          query.state.status === 'success' && query.queryKey[0] !== 'session',
      });
      const snapshot: Snapshot = { buster: BUSTER, savedAt: Date.now(), state };
      window.sessionStorage.setItem(KEY, JSON.stringify(snapshot));
    } catch {
      // Quota or blocked storage: the app works without the snapshot.
    }
  };
  const unsubscribe = client.getQueryCache().subscribe((event) => {
    if (event.type !== 'updated' || timer !== undefined) return;
    timer = window.setTimeout(save, WRITE_DELAY_MS);
  });
  window.addEventListener('pagehide', save);
  return () => {
    unsubscribe();
    window.removeEventListener('pagehide', save);
  };
}

export function clearCache(client: QueryClient): void {
  client.clear();
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    // Nothing stored.
  }
}
